/**
 * Format utilities for the Next.js app
 *
 * Re-exports from @paction-2.0/util where available,
 * and provides app-specific helpers.
 */

// Re-export formatters from util package
export {
  formatCurrency,
  formatCurrencyWithCents,
  formatCents,
  formatDateLabel,
  formatDateShort,
  formatDateLong,
  formatDateUs,
  truncate,
  smartTruncate,
  capitalize,
  titleCase
} from '@paction-2.0/util';

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
};

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const formatNumber = (
  value?: number | null,
  locale = 'en-CA',
  options: Intl.NumberFormatOptions = {}
) => {
  if (!isNumber(value)) return undefined;
  return new Intl.NumberFormat(locale, options).format(value);
};

export const formatRangeLabel = (
  pagination: PaginationMeta,
  noun = 'items'
) => {
  if (pagination.total === 0) {
    return `No ${noun}`;
  }
  const start = (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.page * pagination.limit, pagination.total);
  return `Showing ${start.toLocaleString()} - ${end.toLocaleString()} of ${pagination.total.toLocaleString()} ${noun}`;
};

export const hasMorePages = (
  pagination: PaginationMeta & { totalPages?: number }
) => {
  if (typeof pagination.totalPages === 'number') {
    return pagination.page < pagination.totalPages;
  }
  const estimatedTotalPages = Math.ceil(pagination.total / pagination.limit);
  return pagination.page < estimatedTotalPages;
};

export { isNumber };
